import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { functionHeaders } from './functionSecurity';
import { db } from './firebase';
import { tsToMillis } from './tonight';

export interface JrStaffDesignationResult {
  studentId: string;
  designated: boolean;
  changed: boolean;
  /**
   * JR STAFF PARITY: what this designation did to the dancer's OWN sign-in.
   * true = it gained Jr Staff access, false = it lost it, null = there was
   * nothing to change (no dancer sign-in exists, or the server's parity
   * lane is off). Reported by the server rather than assumed by the screen,
   * so the confirmation can never claim an effect that did not happen.
   */
  dancerLoginPromoted?: boolean | null;
  /** Whether the demoted login's Firebase sessions were successfully killed. */
  sessionsRevoked?: boolean;
}

/**
 * What dancerLoginAccounts/{studentId} really says. 'none' means no doc at
 * all: nobody ever made this dancer sign-in. See
 * netlify/functions/lib/jrStaffLinks.mjs for why a half-finished one is
 * named rather than collapsed into 'none'.
 */
export type JrStaffSignInState = 'none' | 'ready' | 'migration_required' | 'repair_needed';

export interface JrStaffAssignmentStatus {
  studentId: string;
  designated: boolean;
  /**
   * The union: this Jr Staff has a Hub sign-in of their own. Exactly the
   * rule the Jr Staff family view sweep card uses, so the two cards on
   * Manage Users can never contradict each other again.
   */
  activeAccount: boolean;
  /**
   * An ACTIVE staff account carries users/{uid}.studentId for this dancer.
   * Optional so a client rendered before the server field shipped (or a
   * locally constructed placeholder) still type-checks; absent reads false.
   */
  staffAccountLink?: boolean;
  /** An ACTIVE staff account sits in this dancer's students/{id}.familyUids. */
  staffOnFamily?: boolean;
  /** The dancer's own sign-in, by state. Absent reads 'none'. */
  dancerSignIn?: JrStaffSignInState;
}

const FRIENDLY_ERRORS: Record<string, string> = {
  sign_in_required: 'Sign in again, then retry.',
  bad_token: 'Your sign-in expired. Refresh the page and try again.',
  director_only: 'Only an active Director can manage Jr Staff.',
  student_not_found: 'That dancer is no longer on the roster. Refresh and try again.',
  student_identity_missing: 'That older dancer record needs a quick data repair before Jr Staff can be assigned. Nothing changed.',
  jr_staff_designation_required: 'Choose whether this dancer should be assigned Jr Staff.',
  jr_staff_designation_failed: 'The Jr Staff designation could not be saved. Try again.',
  server_not_configured: 'The studio server is missing its Firebase connection.',
  'missing app check token': 'Refresh the page and try again.',
  origin_invalid: 'Refresh the page and try again.',
  valid_uid_required: 'That account could not be identified. Refresh the page and try again.',
  identity_account_not_found: 'That account no longer exists. Refresh the page and try again.',
  identity_account_mismatch: 'That account record needs a quick data repair before it can be linked. Nothing changed.',
  identity_account_inactive: 'That account is not active. Approve or unpause it first, then link.',
  identity_student_canary: 'That dancer record is a test fixture, not a real dancer. Nothing changed.',
  identity_link_in_progress: 'Another link on this account is still finishing. Wait a moment and try again.',
  // identity_family_account and identity_link_conflict deliberately have no
  // entry: the server sends the whole sentence for both, because only the
  // server knows WHICH other account holds the dancer.
};

function friendlyError(code: string, context: string): Error {
  return new Error(FRIENDLY_ERRORS[code] || `${context} hit an error (${code}).`);
}

/**
 * THE REFUSAL THAT CAN BE ACTED ON. identity_link_conflict ends with "clear
 * that account first", and until 2026-09-01 that was an instruction with no
 * control behind it (docs/BUILT-NOT-LIVE.md item 28: the clear itself has
 * been deployed and tested the whole time, and nothing ever called it).
 * The server now names the other account in machine-readable form beside its
 * sentence, so the screen showing the refusal can offer the clear right
 * there rather than sending a Director hunting for it.
 */
export class JrStaffIdentityLinkError extends Error {
  readonly code: string;

  readonly conflictUid: string | null;

  readonly conflictLabel: string | null;

  constructor(
    message: string,
    code: string,
    conflictUid: string | null,
    conflictLabel: string | null,
  ) {
    super(message);
    this.name = 'JrStaffIdentityLinkError';
    this.code = code;
    this.conflictUid = conflictUid;
    this.conflictLabel = conflictLabel;
  }
}

/**
 * The server's own sentence wins when it sent one. A refusal that has to
 * name a second account cannot be reconstructed from a code, and paraphrasing
 * it on the screen is how a Director ends up chasing the wrong account.
 */
function serverError(
  data: { error?: string; message?: string },
  status: number,
  context: string,
): Error {
  if (typeof data.message === 'string' && data.message.trim()) {
    return new Error(data.message.trim());
  }
  return friendlyError(data.error || `server_${status}`, context);
}

export async function fetchStudentJrStaffDesignations(): Promise<JrStaffAssignmentStatus[]> {
  const me = getAuth().currentUser;
  if (!me) throw new Error('Sign in required.');
  const response = await fetch('/.netlify/functions/setStudentJrStaff', {
    method: 'GET',
    credentials: 'same-origin',
    headers: await functionHeaders(me, { json: false }),
    cache: 'no-store',
  });
  const data = (await response.json().catch(() => ({}))) as {
    error?: string;
    statuses?: unknown;
  };
  if (!response.ok || data.error || !Array.isArray(data.statuses)) {
    throw friendlyError(data.error || `server_${response.status}`, 'The Jr Staff list');
  }
  return data.statuses
    .filter((value): value is JrStaffAssignmentStatus => {
      if (!value || typeof value !== 'object') return false;
      const status = value as Partial<JrStaffAssignmentStatus>;
      return typeof status.studentId === 'string'
        && typeof status.designated === 'boolean'
        && typeof status.activeAccount === 'boolean';
    })
    // Normalize the named facts rather than requiring them: a browser
    // holding a cached bundle from before they shipped must still render a
    // truthful card, and a missing fact is honestly "we do not know", which
    // reads the same as absent here.
    .map((status) => ({
      ...status,
      staffAccountLink: status.staffAccountLink === true,
      staffOnFamily: status.staffOnFamily === true,
      dancerSignIn: normalizeSignInState(status.dancerSignIn),
    }));
}

function normalizeSignInState(value: unknown): JrStaffSignInState {
  return value === 'ready' || value === 'migration_required' || value === 'repair_needed'
    ? value
    : 'none';
}

export async function setStudentJrStaffDesignation(
  studentId: string,
  designated: boolean,
): Promise<JrStaffDesignationResult> {
  const me = getAuth().currentUser;
  if (!me) throw new Error('Sign in required.');
  const response = await fetch('/.netlify/functions/setStudentJrStaff', {
    method: 'POST',
    credentials: 'same-origin',
    headers: await functionHeaders(me),
    body: JSON.stringify({ studentId, designated }),
  });
  const data = (await response.json().catch(() => ({}))) as JrStaffDesignationResult & {
    error?: string;
  };
  if (!response.ok || data.error) {
    throw friendlyError(data.error || `server_${response.status}`, 'The Jr Staff designation');
  }
  return data;
}

export interface JrStaffIdentityReservationRecipients {
  /** The class or identity write saved, but the booking refresh did not fully confirm. */
  error: true;
  /** The refresh stopped at its server-enforced scan ceiling. */
  incomplete?: true;
  /** A retry rechecks the nearest booking prefix and cannot reach later bookings. */
  limitReached?: true;
}

export interface JrStaffIdentityLinkResult {
  uid: string;
  studentId: string;
  /** The dancer this account used to be, when this was a repoint. */
  previousStudentId: string | null;
  /** False when the account already carried this dancer: a safe re-tap. */
  changed: boolean;
  /** True when this also added the account to the dancer's familyUids. */
  familyViewGranted: boolean;
  /**
   * The account actually written onto her linked classes, when any were:
   * the classes she assists, and the classes either sign-in is named to teach.
   * NOT necessarily the one the Director tapped: the email WINNER is used, and
   * an older email account outranks a newer one. Bobbie, 2026-09-05, asked for
   * it by name ("Name the account") because "the linked email account" left
   * her unable to tell which of two it meant. Absent when no class changed.
   */
  classAccount: { uid: string; label: string; classes: number } | null;
  /** Present when the identity/class writes saved but booking refresh did not confirm. */
  reservationRecipients: JrStaffIdentityReservationRecipients | null;
}

/**
 * THE IDENTITY LINK: users/{uid}.studentId, "this login IS this dancer".
 *
 * A DIFFERENT LINK from addFamilyLink (lib/students.ts), which writes
 * students/{id}.familyUids, "these people may see this dancer". Manage Users
 * had only the second one, and its Jr Staff coverage list told a Director to
 * fix a missing identity link with it, which could not work (Bobbie, live,
 * 2026-08-27: "But he is linked....."). The server lane and every refusal it
 * makes are documented in netlify/functions/setStudentJrStaff.mjs.
 *
 * A refusal that names another account arrives as a whole sentence and is
 * shown verbatim; nothing here rewrites it.
 */
export async function setJrStaffIdentityLink(
  uid: string,
  studentId: string,
): Promise<JrStaffIdentityLinkResult> {
  const me = getAuth().currentUser;
  if (!me) throw new Error('Sign in required.');
  const response = await fetch('/.netlify/functions/setStudentJrStaff', {
    method: 'POST',
    credentials: 'same-origin',
    headers: await functionHeaders(me),
    body: JSON.stringify({ action: 'identityLink', uid, studentId }),
  });
  const data = (await response.json().catch(() => ({}))) as Partial<JrStaffIdentityLinkResult> & {
    error?: string;
    message?: string;
    conflictUid?: string;
    conflictLabel?: string;
  };
  if (!response.ok || data.error) {
    const failure = serverError(data, response.status, 'The dancer identity link');
    // VERBATIM STILL. The sentence is unchanged and unparsed; the uid rides
    // beside it so a button can act on the very instruction it just gave.
    throw new JrStaffIdentityLinkError(
      failure.message,
      typeof data.error === 'string' ? data.error : `server_${response.status}`,
      typeof data.conflictUid === 'string' && data.conflictUid ? data.conflictUid : null,
      typeof data.conflictLabel === 'string' && data.conflictLabel ? data.conflictLabel : null,
    );
  }
  return {
    uid,
    studentId,
    previousStudentId: typeof data.previousStudentId === 'string' ? data.previousStudentId : null,
    changed: data.changed === true,
    familyViewGranted: data.familyViewGranted === true,
    reservationRecipients: data.reservationRecipients?.error === true
      ? {
        error: true,
        ...(data.reservationRecipients.incomplete === true ? { incomplete: true } : {}),
        ...(data.reservationRecipients.limitReached === true ? { limitReached: true } : {}),
      }
      : null,
    classAccount: data.classAccount
      && typeof data.classAccount.uid === 'string' && data.classAccount.uid
      && typeof data.classAccount.classes === 'number' && data.classAccount.classes > 0
      ? {
        uid: data.classAccount.uid,
        label: typeof data.classAccount.label === 'string' && data.classAccount.label
          ? data.classAccount.label
          : `sign-in ${data.classAccount.uid}`,
        classes: data.classAccount.classes,
      }
      : null,
  };
}

/** One dancer named in a promotion sweep result. */
export interface JrStaffPromotionSweepRow {
  studentId: string;
  firstName: string;
  /** Absent for a dancer the sweep never found a login for. */
  uid?: string;
}

export interface JrStaffPromotionSweepResult {
  /** True when the parity switch is off: the server did not look at anything. */
  skipped: boolean;
  mode: 'report' | 'apply';
  checked: number;
  /** Newly promoted this run (apply), or would-be-promoted (report). */
  promoted: JrStaffPromotionSweepRow[];
  alreadyPromoted: JrStaffPromotionSweepRow[];
  /** Already a real, active staff account linked to this dancer: nothing to do. */
  staffAccount: JrStaffPromotionSweepRow[];
  /** No dancer sign-in yet, or one whose login doc the server would not touch. */
  noLogin: JrStaffPromotionSweepRow[];
}

/**
 * THE ONE-TAP REPAIR for every dancer a Director already tagged Jr Staff in
 * this list, whose OWN sign-in the designation path never promoted: a
 * designation made before JR_STAFF_DANCER_PARITY went live, or one whose
 * promotion write lost a race. netlify/functions/jrStaffPromotionSweep.mjs
 * runs the exact same promotion write setStudentJrStaff.mjs runs at
 * designation time, for every dancer this list already says is Jr Staff.
 *
 * mode 'report' (the default) writes nothing and returns the same shape, so
 * the card can show a true count before anyone taps Confirm.
 */
export async function runJrStaffPromotionSweep(
  mode: 'report' | 'apply' = 'report',
): Promise<JrStaffPromotionSweepResult> {
  const me = getAuth().currentUser;
  if (!me) throw new Error('Sign in required.');
  const response = await fetch('/.netlify/functions/jrStaffPromotionSweep', {
    method: 'POST',
    credentials: 'same-origin',
    headers: await functionHeaders(me),
    body: JSON.stringify({ mode }),
  });
  const data = (await response.json().catch(() => ({}))) as {
    error?: string;
    skipped?: string;
    mode?: string;
    checked?: number;
    promoted?: unknown;
    alreadyPromoted?: unknown;
    staffAccount?: unknown;
    noLogin?: unknown;
  };
  if (!response.ok || data.error) {
    throw friendlyError(data.error || `server_${response.status}`, 'The Jr Staff promotion sweep');
  }
  const rows = (value: unknown): JrStaffPromotionSweepRow[] => (Array.isArray(value)
    ? value
      .filter((row): row is { studentId: unknown; firstName: unknown; uid?: unknown } => (
        !!row && typeof row === 'object'
      ))
      .filter((row) => typeof row.studentId === 'string')
      .map((row) => ({
        studentId: row.studentId as string,
        firstName: typeof row.firstName === 'string' ? row.firstName : '',
        ...(typeof row.uid === 'string' ? { uid: row.uid } : {}),
      }))
    : []);
  return {
    skipped: data.skipped === 'flag_off',
    mode: data.mode === 'apply' ? 'apply' : 'report',
    checked: typeof data.checked === 'number' ? data.checked : 0,
    promoted: rows(data.promoted),
    alreadyPromoted: rows(data.alreadyPromoted),
    staffAccount: rows(data.staffAccount),
    noLogin: rows(data.noLogin),
  };
}

/** The America/Chicago studio clock, the zone every time on this card is shown in. */
const STUDIO_TIME_ZONE = 'America/Chicago';

export interface JrStaffPromotionSweepLastRun {
  /** Epoch millis, or null if the stored timestamp could not be read. */
  ranAt: number | null;
  /** True when the run found the parity switch off and did nothing. */
  skipped: boolean;
  checked: number;
  promotedCount: number;
  alreadyPromotedCount: number;
  staffAccountCount: number;
  noLoginCount: number;
}

/**
 * The scheduled sweep's last run (four times a day through September 30,
 * 2026, then once a month on the 1st just after 2pm Central), for the Jr Staff
 * promotion card.
 * netlify/functions/jrStaffPromotionSweepScheduled.mjs writes this ONE
 * document after every run; this reads it ONCE, never with a listener. The
 * schedule runs on its own clock, not on anyone having the page open, so a
 * live subscription here would only spend reads for no reason.
 *
 * Lives at studioSettings/jrStaffPromotionSweepStatus because zero rules
 * bytes means no new collection could be added for this: studioSettings
 * already reads for any active staff member on any key, and no client write
 * validator recognises this one, so a client write is refused the same as
 * if the rule named it explicitly.
 */
export async function fetchJrStaffPromotionSweepLastRun(): Promise<JrStaffPromotionSweepLastRun | null> {
  if (!db) return null;
  const snap = await getDoc(doc(db, 'studioSettings', 'jrStaffPromotionSweepStatus'));
  if (!snap.exists()) return null;
  const data = snap.data() as Record<string, unknown>;
  const asCount = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);
  return {
    ranAt: tsToMillis(data.ranAt),
    skipped: data.skipped === true,
    checked: asCount(data.checked),
    promotedCount: asCount(data.promotedCount),
    alreadyPromotedCount: asCount(data.alreadyPromotedCount),
    staffAccountCount: asCount(data.staffAccountCount),
    noLoginCount: asCount(data.noLoginCount),
  };
}

/**
 * "Sep 10 at 3:35 PM" in studio time, or '' when there is nothing to say. A
 * runtime with no time zone data falls back to naming the clock it used
 * rather than passing UTC off as the studio's own hour.
 *
 * EVERY STORED COUNT, NOT ONLY promotedCount AND checked (Codex review of
 * #1102, P2). The scheduled function stores all five buckets
 * (jrStaffPromotionSweepScheduled.mjs), and a Director reading only
 * "promoted" and "checked" cannot tell 0 promoted with 4 already promoted
 * (nothing wrong, everyone already had it) from 0 promoted with 4 no login
 * (4 dancers with no sign-in to promote at all) - both read as "gave access
 * to 0, 4 checked" if the other buckets are silent. Each nonzero bucket
 * gets its own clause instead, in the same plain words the card's own
 * Confirm result already uses (Promoted / Already had it / Already a staff
 * account / No sign-in yet).
 */
export function describeJrStaffPromotionSweepLastRun(run: JrStaffPromotionSweepLastRun | null): string {
  if (!run || run.ranAt == null) return '';
  const when = new Date(run.ranAt);
  let stamp: string;
  try {
    const day = new Intl.DateTimeFormat('en-US', {
      timeZone: STUDIO_TIME_ZONE, month: 'short', day: 'numeric',
    }).format(when);
    const time = new Intl.DateTimeFormat('en-US', {
      timeZone: STUDIO_TIME_ZONE, hour: 'numeric', minute: '2-digit',
    }).format(when);
    stamp = `${day} at ${time}`;
  } catch {
    stamp = `${when.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
  }
  if (run.skipped) return `The automatic check last ran ${stamp}. The switch for this was off then.`;
  if (run.checked === 0) return `The automatic check last ran ${stamp}: no dancers are tagged Jr Staff yet.`;
  const parts: string[] = [];
  if (run.promotedCount > 0) parts.push(`gave access to ${run.promotedCount}`);
  if (run.alreadyPromotedCount > 0) parts.push(`${run.alreadyPromotedCount} already had it`);
  if (run.staffAccountCount > 0) parts.push(`${run.staffAccountCount} already on a staff account`);
  if (run.noLoginCount > 0) parts.push(`${run.noLoginCount} with no sign-in yet`);
  return `The automatic check last ran ${stamp}: ${parts.join(', ')}, ${run.checked} checked.`;
}

export interface JrStaffIdentityClearResult {
  uid: string;
  studentId: string;
  /** True only when a stale link was actually removed. */
  cleared: boolean;
  /** The server's own word for what it did, never inferred here. */
  outcome: string;
  /** The account it cleared, named the way the server names people. */
  accountLabel: string;
  dancerName: string;
  /**
   * J7, live unlink only: the teaching seats this exact link had granted,
   * read back from users/{uid}/jrStaffTeachingSeatGrants and taken off any
   * class that still carried them. Absent for the orphan-clear door, which
   * never touches classes. A class linked before this shipped has no grant
   * on record and is not in either list - it is not a skip, it is simply
   * invisible to this door, same as it was before.
   *
   * NO error FIELD (removed after the P1#2 fix, Codex review of PR #1104):
   * the seat revoke now runs in the SAME transaction as the identity clear
   * itself (recoverDancerLinkOperation, setDancerLink.mjs), so a failure
   * here means the whole call rejects and studentId is untouched - the
   * generic catch in unlinkLiveIdentity below already covers that case
   * honestly, and a field that can never come back populated is not one to
   * keep around looking like a real behaviour.
   */
  teachingSeats: {
    removedOfferingIds: string[];
    skippedOfferingIds: string[];
  } | null;
}

/**
 * CLEAR A STALE IDENTITY LINK: the door for setDancerLink's orphan-recovery
 * transaction, which has been deployed, Director-gated and tested since it
 * shipped and which nothing had ever called.
 *
 * It clears ONLY a link held by a sign-in that no longer exists. A live
 * sign-in, and a dancer's own family-made sign-in, are both refused by name
 * and nothing is written, so this is not a way to take a dancer off a login
 * she is still using. Those refusals arrive as whole sentences and are shown
 * verbatim, same as the conflict above.
 */
export async function clearJrStaffIdentityLink(
  uid: string,
  studentId: string,
): Promise<JrStaffIdentityClearResult> {
  const me = getAuth().currentUser;
  if (!me) throw new Error('Sign in required.');
  const response = await fetch('/.netlify/functions/setStudentJrStaff', {
    method: 'POST',
    credentials: 'same-origin',
    headers: await functionHeaders(me),
    body: JSON.stringify({ action: 'clearIdentityLink', uid, studentId }),
  });
  const data = (await response.json().catch(() => ({}))) as Partial<JrStaffIdentityClearResult> & {
    error?: string;
    message?: string;
  };
  if (!response.ok || data.error) {
    throw serverError(data, response.status, 'Clearing the old sign-in');
  }
  return {
    uid,
    studentId,
    cleared: data.cleared === true,
    outcome: typeof data.outcome === 'string' ? data.outcome : 'none',
    accountLabel: typeof data.accountLabel === 'string' ? data.accountLabel : `sign-in ${uid}`,
    dancerName: typeof data.dancerName === 'string' ? data.dancerName : studentId,
    // The orphan door never touches classes.
    teachingSeats: null,
  };
}

function readTeachingSeats(
  data: Partial<JrStaffIdentityClearResult>,
): JrStaffIdentityClearResult['teachingSeats'] {
  const raw = data.teachingSeats as {
    removedOfferingIds?: unknown; skippedOfferingIds?: unknown;
  } | null | undefined;
  if (!raw || typeof raw !== 'object') return null;
  const ids = (value: unknown) => Array.isArray(value)
    ? value.filter((id): id is string => typeof id === 'string' && id.length > 0)
    : [];
  return {
    removedOfferingIds: ids(raw.removedOfferingIds),
    skippedOfferingIds: ids(raw.skippedOfferingIds),
  };
}

/** R48 phase one: detach only the dancer identity from a working account. */
export async function clearLiveJrStaffIdentityLink(uid: string, studentId: string): Promise<JrStaffIdentityClearResult> {
  const me = getAuth().currentUser;
  if (!me) throw new Error('Sign in required.');
  const response = await fetch('/.netlify/functions/setStudentJrStaff', {
    method: 'POST', credentials: 'same-origin', headers: await functionHeaders(me),
    body: JSON.stringify({ action: 'clearLiveIdentityLink', uid, studentId, confirmed: true }),
  });
  const data = await response.json().catch(() => ({})) as Partial<JrStaffIdentityClearResult> & { error?: string; message?: string };
  if (!response.ok || data.error || data.cleared !== true) {
    throw serverError(data, response.status, 'Unlinking the dancer identity');
  }
  return {
    uid,
    studentId,
    cleared: true,
    outcome: typeof data.outcome === 'string' ? data.outcome : 'live_identity_cleared',
    accountLabel: typeof data.accountLabel === 'string' ? data.accountLabel : `sign-in ${uid}`,
    dancerName: typeof data.dancerName === 'string' ? data.dancerName : studentId,
    teachingSeats: readTeachingSeats(data),
  };
}
